import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { addMonths, subMonths } from 'date-fns';
import { Info } from 'lucide-react';

// Components
import CalendarHeader from './components/CalendarHeader';
import CalendarGrid from './components/CalendarGrid';
import CalendarList from './components/CalendarList';
import CalendarEventForm from './CalendarEventForm';
import SettingsHeader from '../../ui/SettingsHeader';
import ConfirmationModal from '../../ui/ConfirmationModal';
import { createNotice, sendNotice } from '../../api/services/noticeApi';

const Calendar = () => {
  const { user, isAdmin, isTeacher } = useAuth();
  const { calendar: events, addCalendarEvent, updateCalendarEvent, deleteCalendarEvent } = useData();
  
  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, eventId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Safe data check
  const safeEvents = Array.isArray(events) ? events : [];

  // Handlers
  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const handleDateClick = (date) => {
    setPrefilledDate(date);
    setSelectedEvent(null);
    setShowAddModal(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setPrefilledDate(null);
    setShowEditModal(true);
  };

  const handleAddEvent = async (eventData) => {
    try {
      const { createAlsoAsNotice, autoSendNotice, ...calendarData } = eventData;
      
      const result = await addCalendarEvent({
        ...calendarData,
        createdBy: user?._id
      });

      if (result.success) {
        setShowAddModal(false);
        setPrefilledDate(null);
        toast.success('Event added successfully!');

        // Logic to create notice if requested
        if (createAlsoAsNotice) {
          try {
            const noticePayload = {
              title: calendarData.title,
              content: calendarData.description || `Event: ${calendarData.title}`,
              category: 'Announcement',
              priority: 'Medium',
              status: autoSendNotice ? 'sent' : 'draft',
              applicableUnits: calendarData.applicableClasses || [],
              applicableSections: [],
              type: 'notice',
              eventDate: calendarData.startDate,
              whatsappTemplate: calendarData.whatsappTemplate,
              whatsappParams: calendarData.whatsappParams
            };

            const noticeRes = await createNotice(noticePayload);
            const noticeId = noticeRes?.data?._id || noticeRes?.data?.id || noticeRes?._id || noticeRes?.id;

            if (autoSendNotice && noticeId) {
              await sendNotice(noticeId);
              toast.success('Notice also created and broadcasted!');
            } else {
              toast.success('Notice also created as draft!');
            }
          } catch (err) {
            toast.error('Event created, but failed to create notice.');
          }
        }
      } else {
        toast.error(`Failed to add event: ${result.message}`);
      }
    } catch (error) {
      toast.error('Failed to add event. Please try again.');
    }
  };

  const handleEditEvent = async (eventData) => {
    try {
      const result = await updateCalendarEvent(selectedEvent._id, eventData);
      if (result.success) {
        setShowEditModal(false);
        setSelectedEvent(null);
        toast.success('Event updated successfully!');
      } else {
        toast.error(`Failed to update event: ${result.message}`);
      }
    } catch (error) {
      toast.error('Failed to update event. Please try again.');
    }
  };

  const handleDeleteEvent = (eventId) => {
    setDeleteModal({ open: true, eventId });
  };

  const confirmDeleteEvent = async () => {
    if (!deleteModal.eventId) return;
    setIsDeleting(true);
    try {
      const result = await deleteCalendarEvent(deleteModal.eventId);
      if (result.success || !result.message) {
        setShowEditModal(false);
        setSelectedEvent(null);
        setDeleteModal({ open: false, eventId: null });
        toast.success('Event deleted successfully!');
      } else {
        toast.error(`Failed to delete event: ${result.message}`);
      }
    } catch (error) {
      toast.error('Failed to delete event. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAdmin && !isTeacher) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Info className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only teachers and administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CalendarHeader 
        currentDate={currentDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onAddEvent={() => {
          setPrefilledDate(new Date());
          setShowAddModal(true);
        }}
      />

      <div className="animate-in fade-in duration-300">
        {viewMode === 'grid' ? (
          <CalendarGrid 
            currentDate={currentDate}
            events={safeEvents}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
          />
        ) : (
          <CalendarList 
            events={safeEvents}
            currentDate={currentDate}
            onEventClick={handleEventClick}
          />
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <CalendarEventForm
          initialDate={prefilledDate}
          onClose={() => {
            setShowAddModal(false);
            setPrefilledDate(null);
          }}
          onSave={handleAddEvent}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEvent && (
        <CalendarEventForm
          event={selectedEvent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
          }}
          onSave={handleEditEvent}
          onDelete={() => handleDeleteEvent(selectedEvent._id)}
        />
      )}
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, eventId: null })}
        onConfirm={confirmDeleteEvent}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone and will remove it from the calendar."
        confirmText="Delete Event"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Calendar;
